import React, { useEffect, useState } from "react";
import {
  Table, Tag, Input, Select, DatePicker, Button, Modal, message,
  Dropdown, Space, Tooltip, Drawer, Divider, Badge, Typography,
} from "antd";
import {
  SearchOutlined, WalletOutlined, CheckOutlined, CloseOutlined,
  EyeOutlined, ReloadOutlined, FileExcelOutlined, FilePdfOutlined,
  DownOutlined, UpOutlined, ClockCircleOutlined, CheckCircleOutlined,
  CloseCircleOutlined, SyncOutlined, DollarOutlined,
  UserOutlined, BankOutlined, CalendarOutlined, IdcardOutlined,
  FileTextOutlined, MailOutlined, PhoneOutlined, CreditCardOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import {
  AdminGetWithdrawals,
  AdminUpdateWithdrawalStatus,
  AdminDeleteWithdrawal,
} from "../../../../Api/CoreApi";
import { exportToCSV } from "../../Utils/exportCSV";
import { exportToPDF } from "../../Utils/exportPDF";
import {
  formatDate, formatDateTime,
  generateRefNumber, capitalize,
} from "../../Utils/formatters";
import "./Withdrawals.css";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

function Withdrawals() {
  // ============================================
  // STATES
  // ============================================
  const [allWithdrawals, setAllWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);

  // History Filters (Table 1 - Upper)
  const [historySearchText, setHistorySearchText] = useState("");
  const [historyDateRange, setHistoryDateRange] = useState(null);

  // Request Filters (Table 2 - Lower)
  const [requestStatusFilter, setRequestStatusFilter] = useState("all");
  const [requestUserType, setRequestUserType] = useState("all");
  const [requestSearchText, setRequestSearchText] = useState("");

  // Drawers
  const [requestDrawerOpen, setRequestDrawerOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);

  // 📱 Mobile View States
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showTopStats, setShowTopStats] = useState(false);
  const [showBottomStats, setShowBottomStats] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ============================================
  // FETCH ALL WITHDRAWALS
  // ============================================
  const fetchAllWithdrawals = async () => {
    try {
      setLoading(true);
      const data = await AdminGetWithdrawals({});
      setAllWithdrawals(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("❌ Fetch error:", error);
      message.error("Failed to fetch withdrawals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllWithdrawals();
  }, []);

  // ============================================
  // ✅ TABLE 1 - HISTORY
  // ============================================
  const getHistoryData = () => {
    let data = allWithdrawals.filter(
      (w) => w.status === "completed" || w.status === "approved" || w.status === "processing"
    );

    if (historySearchText) {
      const s = historySearchText.toLowerCase();
      data = data.filter(
        (r) =>
          r.user_name?.toLowerCase().includes(s) ||
          r.username?.toLowerCase().includes(s) ||
          r.contact?.includes(historySearchText) ||
          generateRefNumber("withdrawal", r.id).toLowerCase().includes(s) ||
          String(r.amount).includes(historySearchText)
      );
    }

    if (historyDateRange && historyDateRange.length === 2) {
      data = data.filter((r) => {
        const d = new Date(r.processed_at || r.created_at);
        return d >= historyDateRange[0].toDate() && d <= historyDateRange[1].toDate();
      });
    }

    return data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  // ============================================
  // ✅ TABLE 2 - REQUESTS
  // ============================================
  const getRequestsData = () => {
    let data = [...allWithdrawals];

    if (requestStatusFilter !== "all") {
      data = data.filter((r) => r.status === requestStatusFilter);
    }

    if (requestUserType !== "all") {
      data = data.filter((r) => r.role === requestUserType);
    }

    if (requestSearchText) {
      const s = requestSearchText.toLowerCase();
      data = data.filter(
        (r) =>
          r.user_name?.toLowerCase().includes(s) ||
          r.username?.toLowerCase().includes(s) ||
          String(r.amount).includes(requestSearchText)
      );
    }

    return data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  // ============================================
  // STATS
  // ============================================
  const getHistoryStats = () => {
    const data = getHistoryData();
    return {
      total: data.length,
      totalAmount: data.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0),
      approved: data.filter((r) => r.status === "approved").length,
      processing: data.filter((r) => r.status === "processing").length,
      completed: data.filter((r) => r.status === "completed").length,
    };
  };

  const getRequestStats = () => {
    return {
      total: allWithdrawals.length,
      pending: allWithdrawals.filter((r) => r.status === "pending").length,
      approved: allWithdrawals.filter((r) => r.status === "approved").length,
      processing: allWithdrawals.filter((r) => r.status === "processing").length,
      completed: allWithdrawals.filter((r) => r.status === "completed").length,
      rejected: allWithdrawals.filter((r) => r.status === "rejected").length,
      pendingAmount: allWithdrawals.filter((r) => r.status === "pending").reduce((s, r) => s + (parseFloat(r.amount) || 0), 0),
    };
  };

  // ============================================
  // ACTIONS
  // ============================================
  const handleRequestAction = async (request, status, adminNote = "") => {
    try {
      setActionLoading(true);
      const result = await AdminUpdateWithdrawalStatus(request.id, status, adminNote);

      if (result.success) {
        message.success(result.message || `Withdrawal ${status}!`);
        await fetchAllWithdrawals();
        setRequestDrawerOpen(false);
      } else {
        message.error(result.message || "Failed to update status");
      }
    } catch (error) {
      message.error(error.message || "Failed to update status");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectWithNote = (request) => {
    let note = "";
    Modal.confirm({
      title: "Reject Withdrawal Request",
      content: (
        <div>
          <p>Please provide a reason for rejection:</p>
          <Input.TextArea placeholder="Enter rejection reason..." rows={3} onChange={(e) => { note = e.target.value; }} />
        </div>
      ),
      okText: "Reject",
      okType: "danger",
      onOk: () => handleRequestAction(request, "rejected", note),
    });
  };

  const handleDeleteRequest = (request) => {
    Modal.confirm({
      title: "Delete Withdrawal Request?",
      content: (
        <div>
          <p>Are you sure you want to delete this request?</p>
          <div style={{ padding: 10, background: "#fff1f0", borderRadius: 6 }}>
            <div><strong>Amount:</strong> ₹{parseFloat(request.amount || 0).toLocaleString("en-IN")}</div>
            <div><strong>User:</strong> {request.user_name || request.username}</div>
          </div>
        </div>
      ),
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        try {
          await AdminDeleteWithdrawal(request.id);
          message.success("Request deleted successfully!");
          await fetchAllWithdrawals();
        } catch (error) {
          message.error("Failed to delete request");
        }
      },
    });
  };

  const clearHistoryFilters = () => { setHistorySearchText(""); setHistoryDateRange(null); };
  const clearRequestFilters = () => { setRequestSearchText(""); setRequestStatusFilter("all"); setRequestUserType("all"); };

  // ============================================
  // STATUS CONFIG
  // ============================================
  const getStatusConfig = (status) => {
    const config = {
      pending: { color: "pending", icon: <ClockCircleOutlined />, label: "Pending" },
      approved: { color: "approved", icon: <CheckCircleOutlined />, label: "Approved" },
      processing: { color: "processing", icon: <SyncOutlined spin />, label: "Processing" },
      completed: { color: "completed", icon: <CheckCircleOutlined />, label: "Completed" },
      rejected: { color: "rejected", icon: <CloseCircleOutlined />, label: "Rejected" },
    };
    return config[status] || config.pending;
  };

  // ============================================
  // COLUMNS
  // ============================================
  const historyColumns = [
    { title: "#", key: "sno", width: 45, fixed: "left", render: (_, __, i) => <span>{i + 1}</span> },
    { title: "Ref No", key: "ref", width: 120, render: (_, r) => <Text code style={{ fontSize: 12 }}>{generateRefNumber("withdrawal", r.id)}</Text> },
    {
      title: "User", key: "user", width: 180,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.user_name || r.username || "N/A"}</div>
          <div style={{ fontSize: 11, color: "#888" }}>
            <Tag style={{ fontSize: 10, margin: 0 }}>{r.role === "staff" ? "👔 Staff" : r.role === "developer" ? "💻 Developer" : "👤 User"}</Tag>
          </div>
        </div>
      ),
    },
    { title: "Amount", key: "amount", width: 120, render: (_, r) => <span style={{ fontWeight: 600, color: "#52c41a" }}>₹{parseFloat(r.amount || 0).toLocaleString("en-IN")}</span> },
    { title: "Payment Details", key: "bank", width: 180, render: (_, r) => <div><div style={{ fontSize: 13 }}>{r.bank_account || r.upi_id || "N/A"}</div><div style={{ fontSize: 11, color: "#888" }}>{r.ifsc_code || ""}</div></div> },
    { title: "Status", key: "status", width: 120, render: (_, r) => { const cfg = getStatusConfig(r.status); return <Tag className={`admin-withdrawals-status-tag admin-withdrawals-status-${cfg.color}`}>{cfg.icon} {cfg.label}</Tag>; } },
    { title: "Processed Date", key: "date", width: 130, render: (_, r) => <div><div style={{ fontSize: 13 }}>{formatDate(r.processed_at || r.created_at)}</div></div> },
    { title: "Action", key: "action", width: 80, fixed: "right", render: (_, record) => <Tooltip title="View Details"><Button size="small" icon={<EyeOutlined />} onClick={() => { setSelectedHistory(record); setHistoryDrawerOpen(true); }} /></Tooltip> },
  ];

  const requestColumns = [
    { title: "#", key: "sno", width: 45, render: (_, __, i) => <span>{i + 1}</span> },
    { title: "Ref", key: "ref", width: 120, render: (_, r) => <Text code style={{ fontSize: 12 }}>{generateRefNumber("withdrawal", r.id)}</Text> },
    {
      title: "User", key: "user", width: 180,
      render: (_, r) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: r.role === "staff" ? "#e6f7ff" : r.role === "developer" ? "#f6ffed" : "#f0f5ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 600, color: r.role === "staff" ? "#1890ff" : r.role === "developer" ? "#52c41a" : "#2f54eb" }}>
            {(r.user_name?.[0] || r.username?.[0] || "U").toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 500 }}>{r.user_name || r.username}</div>
            <div style={{ fontSize: 11, color: "#888" }}><Tag style={{ margin: 0, fontSize: 10 }}>{r.role === "staff" ? "👔 Staff" : r.role === "developer" ? "💻 Developer" : "👤 User"}</Tag></div>
          </div>
        </div>
      ),
    },
    { title: "Contact", key: "contact", width: 140, render: (_, r) => <div><div style={{ fontSize: 13 }}>{r.contact || "N/A"}</div><div style={{ fontSize: 11, color: "#888" }}>{r.email || ""}</div></div> },
    { title: "Amount", key: "amount", width: 120, render: (_, r) => <span style={{ fontWeight: 600, color: "#ff0055" }}>₹{parseFloat(r.amount || 0).toLocaleString("en-IN")}</span> },
    { title: "Bank Details", key: "bank", width: 180, render: (_, r) => <div><div style={{ fontSize: 13 }}>{r.bank_account || r.upi_id || "N/A"}</div><div style={{ fontSize: 11, color: "#888" }}>{r.ifsc_code || ""}</div></div> },
    { title: "Status", key: "status", width: 120, render: (_, r) => { const cfg = getStatusConfig(r.status); return <Tag className={`admin-withdrawals-status-tag admin-withdrawals-status-${cfg.color}`}>{cfg.icon} {cfg.label}</Tag>; } },
    { title: "Requested", key: "date", width: 110, render: (_, r) => <div><div style={{ fontSize: 13 }}>{formatDate(r.created_at)}</div></div> },
    {
      title: "Actions", key: "action", width: 100, fixed: "right",
      render: (_, record) => (
        <Space size={3}>
          <Tooltip title="View"><Button size="small" icon={<EyeOutlined />} onClick={() => { setSelectedRequest(record); setRequestDrawerOpen(true); }} /></Tooltip>
          {record.status === "pending" && (
            <>
              <Tooltip title="Approve"><Button size="small" icon={<CheckOutlined />} style={{ background: "#52c41a", color: "white", borderColor: "#52c41a" }} onClick={() => handleRequestAction(record, "approved")} /></Tooltip>
              <Tooltip title="Reject"><Button size="small" danger icon={<CloseOutlined />} onClick={() => handleRejectWithNote(record)} /></Tooltip>
            </>
          )}
          {record.status === "approved" && (
            <Tooltip title="Process">
              <Button size="small" icon={<SyncOutlined />} style={{ background: "#1890ff", color: "white", borderColor: "#1890ff" }} onClick={() => handleRequestAction(record, "processing")}>
                <span className="hide-on-mobile">Process</span>
              </Button>
            </Tooltip>
          )}
          {record.status === "processing" && (
            <Tooltip title="Complete">
              <Button size="small" icon={<CheckCircleOutlined />} style={{ background: "#52c41a", color: "white", borderColor: "#52c41a" }} onClick={() => handleRequestAction(record, "completed")}>
                <span className="hide-on-mobile">Complete</span>
              </Button>
            </Tooltip>
          )}
          {(record.status === "completed" || record.status === "rejected") && (
            <Tooltip title="Delete"><Button size="small" danger icon={<CloseOutlined />} onClick={() => handleDeleteRequest(record)} /></Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const historyStats = getHistoryStats();
  const requestStats = getRequestStats();

  const exportColumns = [
    { key: "ref", label: "Ref", format: (r) => generateRefNumber("withdrawal", r.id) },
    { key: "user", label: "User", format: (r) => r.user_name || r.username || "N/A" },
    { key: "amount", label: "Amount", format: (r) => `₹${parseFloat(r.amount || 0).toFixed(2)}` },
    { key: "status", label: "Status", format: (r) => capitalize(r.status) },
  ];

  return (
    <div className="admin-withdrawals-page">

      {/* ============ HEADER ============ */}
      <div className="admin-withdrawals-header-stats-wrapper">
        <div className="admin-withdrawals-page-header">
          <div className="admin-withdrawals-header-left">
            <div className="admin-withdrawals-title-row">
              <div className="admin-withdrawals-title-icon"><WalletOutlined /></div>
              <div className="admin-withdrawals-title-content">
                <h2 className="admin-withdrawals-page-title">Withdrawal Management</h2>
                <p className="admin-withdrawals-page-subtitle">
                  {historyStats.total} completed • ₹{historyStats.totalAmount.toLocaleString("en-IN")}
                </p>
              </div>
            </div>
          </div>
          <div className="admin-withdrawals-header-actions">
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchAllWithdrawals} loading={loading} className="icon-mobile-btn" />
            </Tooltip>
            <Dropdown menu={{ items: [{ key: "1", label: `Export History`, onClick: () => exportToCSV(getHistoryData(), exportColumns, "withdrawal_history") }] }}>
              <Button icon={<FileExcelOutlined />} className="admin-withdrawals-csv-btn icon-mobile-btn">
                <span className="hide-on-mobile">CSV</span>
              </Button>
            </Dropdown>
            {isMobile && (
              <Button
                className="toggle-stats-btn icon-mobile-btn"
                onClick={() => setShowTopStats(!showTopStats)}
                icon={showTopStats ? <UpOutlined /> : <DownOutlined />}
              />
            )}
          </div>
        </div>

        {/* TOP STATS GRID */}
        <div className={`stats-grid-wrapper ${!isMobile || showTopStats ? 'show' : 'hide'}`}>
          <div className="stat-grid-item">
            <HistoryOutlined style={{ color: '#ff0055' }} />
            <div className="stat-grid-content"><b>{historyStats.total}</b><span>PROCESSED</span></div>
          </div>
          <div className="stat-grid-item">
            <CheckCircleOutlined style={{ color: '#1890ff' }} />
            <div className="stat-grid-content"><b>{historyStats.approved}</b><span>APPROVED</span></div>
          </div>
          <div className="stat-grid-item">
            <SyncOutlined style={{ color: '#722ed1' }} />
            <div className="stat-grid-content"><b>{historyStats.processing}</b><span>PROCESSING</span></div>
          </div>
          <div className="stat-grid-item">
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
            <div className="stat-grid-content"><b>{historyStats.completed}</b><span>COMPLETED</span></div>
          </div>
          <div className="stat-grid-item">
            <DollarOutlined style={{ color: '#faad14' }} />
            <div className="stat-grid-content"><b>₹{historyStats.totalAmount.toLocaleString("en-IN")}</b><span>TOTAL PAID</span></div>
          </div>
        </div>
      </div>

      {/* ============================================================
          ✅ TABLE 1 - HISTORY
          ============================================================ */}
      <div className="admin-withdrawals-table-card" style={{ marginTop: 24 }}>
        <div className="table-header-controls">
          <div className="table-title">
            <HistoryOutlined /> <h3>Processed History</h3>
            <Badge count={historyStats.total} style={{ backgroundColor: "#52c41a" }} />
          </div>
          <div className="filter-row-split mobile-filters">
            <Input placeholder="Search history..." prefix={<SearchOutlined />} value={historySearchText} onChange={(e) => setHistorySearchText(e.target.value)} className="mini-search-box filter-col full-width" allowClear />
            <div className="filter-row-split full-width">
              <RangePicker value={historyDateRange} onChange={setHistoryDateRange} className="app-datepicker filter-col half-width" />
              <Button className="app-clear-btn filter-col half-width" onClick={clearHistoryFilters}>Clear</Button>
            </div>
          </div>
        </div>

        <Table rowKey={(record) => `history-${record.id}`} columns={historyColumns} dataSource={getHistoryData()} loading={loading} size="small" pagination={{ pageSize: 10 }} scroll={{ x: 1100 }} className="admin-withdrawals-table" />
      </div>

      {/* ============================================================
          ✅ TABLE 2 - REQUESTS
          ============================================================ */}
      <div className="admin-withdrawals-table-card" style={{ marginTop: 24 }}>
        <div className="table-header-controls" style={{ paddingBottom: 0, borderBottom: 'none' }}>
          <div className="table-title" style={{ width: '100%', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <WalletOutlined /> <h3>Withdrawal Requests</h3>
              <Badge count={requestStats.pending} style={{ backgroundColor: "#faad14" }} />
            </div>
            {isMobile && (
              <Button size="small" onClick={() => setShowBottomStats(!showBottomStats)} icon={showBottomStats ? <UpOutlined /> : <DownOutlined />} style={{ borderRadius: 8 }} />
            )}
          </div>
        </div>

        {/* BOTTOM STATS GRID */}
        <div className={`stats-grid-wrapper ${!isMobile || showBottomStats ? 'show' : 'hide'}`} style={{ margin: '10px 20px', background: '#fafbfc' }}>
          <div className="stat-grid-item"><WalletOutlined style={{ color: '#ff0055' }} /><div className="stat-grid-content"><b>{requestStats.total}</b><span>TOTAL</span></div></div>
          <div className="stat-grid-item"><ClockCircleOutlined style={{ color: '#faad14' }} /><div className="stat-grid-content"><b>{requestStats.pending}</b><span>PENDING</span></div></div>
          <div className="stat-grid-item"><CloseCircleOutlined style={{ color: '#f5222d' }} /><div className="stat-grid-content"><b>{requestStats.rejected}</b><span>REJECTED</span></div></div>
          <div className="stat-grid-item"><DollarOutlined style={{ color: '#ff0055' }} /><div className="stat-grid-content"><b>₹{requestStats.pendingAmount.toLocaleString("en-IN")}</b><span>PENDING AMT</span></div></div>
        </div>

        <div className="table-header-controls pt-0">
          <div className="filter-row-split mobile-filters" style={{ width: '100%' }}>
            <Input placeholder="Search user, amount..." prefix={<SearchOutlined />} value={requestSearchText} onChange={(e) => setRequestSearchText(e.target.value)} className="mini-search-box filter-col full-width" allowClear />
            <div className="filter-row-split full-width">
              <Select value={requestStatusFilter} onChange={setRequestStatusFilter} className="app-select filter-col half-width">
                <Option value="all">All Status</Option>
                <Option value="pending">⏳ Pending</Option>
                <Option value="approved">✅ Approved</Option>
                <Option value="processing">🔄 Process</Option>
                <Option value="completed">✔️ Done</Option>
                <Option value="rejected">❌ Reject</Option>
              </Select>
              <Select value={requestUserType} onChange={setRequestUserType} className="app-select filter-col half-width">
                <Option value="all">All Users</Option>
                <Option value="user">👤 User</Option>
                <Option value="staff">👔 Staff</Option>
                <Option value="developer">💻 Dev</Option>
              </Select>
            </div>
            <Button className="app-clear-btn full-width" onClick={clearRequestFilters} style={{ marginTop: 8 }}>Clear Filters</Button>
          </div>
        </div>

        <Table rowKey={(record) => `req-${record.id}`} columns={requestColumns} dataSource={getRequestsData()} loading={loading} size="middle" pagination={{ pageSize: 10 }} scroll={{ x: 1300 }} className="admin-withdrawals-table" />
      </div>

      {/* ============ HISTORY DRAWER ============ */}
      <Drawer
        title={
          <div className="wd-drawer-title">
            <div className="wd-drawer-title-icon"><HistoryOutlined /></div>
            <div>
              <h3>Withdrawal Details</h3>
              <p>Processed transaction info</p>
            </div>
          </div>
        }
        placement="right"
        width={isMobile ? '100%' : 480}
        onClose={() => setHistoryDrawerOpen(false)}
        open={historyDrawerOpen}
        className="wd-custom-drawer"
      >
        {selectedHistory && (
          <div className="wd-drawer-content">
            {/* Amount Hero */}
            <div className="wd-amount-hero completed">
              <div className="wd-amount-icon">
                <CheckCircleOutlined />
              </div>
              <div className="wd-amount-value">
                ₹{parseFloat(selectedHistory.amount || 0).toLocaleString("en-IN")}
              </div>
              <div className="wd-amount-ref">
                {generateRefNumber("withdrawal", selectedHistory.id)}
              </div>
              <Tag className={`admin-withdrawals-status-tag admin-withdrawals-status-${getStatusConfig(selectedHistory.status).color}`}>
                {getStatusConfig(selectedHistory.status).icon} {getStatusConfig(selectedHistory.status).label}
              </Tag>
            </div>

            {/* User Info */}
            <div className="wd-detail-section">
              <h4><UserOutlined /> User Info</h4>
              <div className="wd-detail-item">
                <div className="wd-detail-icon-wrap"><UserOutlined /></div>
                <div className="wd-detail-label">Name</div>
                <div className="wd-detail-value">{selectedHistory.user_name || selectedHistory.username || "N/A"}</div>
              </div>
              <div className="wd-detail-item">
                <div className="wd-detail-icon-wrap"><IdcardOutlined /></div>
                <div className="wd-detail-label">Role</div>
                <div className="wd-detail-value">
                  <Tag color={selectedHistory.role === 'staff' ? 'blue' : selectedHistory.role === 'developer' ? 'green' : 'default'}>
                    {selectedHistory.role === 'staff' ? '👔 Staff' : selectedHistory.role === 'developer' ? '💻 Developer' : '👤 User'}
                  </Tag>
                </div>
              </div>
              {selectedHistory.contact && (
                <div className="wd-detail-item">
                  <div className="wd-detail-icon-wrap"><PhoneOutlined /></div>
                  <div className="wd-detail-label">Contact</div>
                  <div className="wd-detail-value">{selectedHistory.contact}</div>
                </div>
              )}
              {selectedHistory.email && (
                <div className="wd-detail-item">
                  <div className="wd-detail-icon-wrap"><MailOutlined /></div>
                  <div className="wd-detail-label">Email</div>
                  <div className="wd-detail-value">{selectedHistory.email}</div>
                </div>
              )}
            </div>

            {/* Payment Info */}
            <div className="wd-detail-section">
              <h4><BankOutlined /> Payment Details</h4>
              {selectedHistory.bank_account && (
                <div className="wd-detail-item">
                  <div className="wd-detail-icon-wrap"><BankOutlined /></div>
                  <div className="wd-detail-label">Bank Acc</div>
                  <div className="wd-detail-value mono">{selectedHistory.bank_account}</div>
                </div>
              )}
              {selectedHistory.ifsc_code && (
                <div className="wd-detail-item">
                  <div className="wd-detail-icon-wrap"><IdcardOutlined /></div>
                  <div className="wd-detail-label">IFSC Code</div>
                  <div className="wd-detail-value mono">{selectedHistory.ifsc_code}</div>
                </div>
              )}
              {selectedHistory.upi_id && (
                <div className="wd-detail-item">
                  <div className="wd-detail-icon-wrap"><CreditCardOutlined /></div>
                  <div className="wd-detail-label">UPI ID</div>
                  <div className="wd-detail-value mono">{selectedHistory.upi_id}</div>
                </div>
              )}
            </div>

            {/* Timing */}
            <div className="wd-detail-section">
              <h4><CalendarOutlined /> Timing</h4>
              <div className="wd-detail-item">
                <div className="wd-detail-icon-wrap"><CalendarOutlined /></div>
                <div className="wd-detail-label">Requested</div>
                <div className="wd-detail-value">{formatDateTime(selectedHistory.created_at)}</div>
              </div>
              {selectedHistory.processed_at && (
                <div className="wd-detail-item">
                  <div className="wd-detail-icon-wrap"><CheckCircleOutlined /></div>
                  <div className="wd-detail-label">Processed</div>
                  <div className="wd-detail-value">{formatDateTime(selectedHistory.processed_at)}</div>
                </div>
              )}
            </div>

            {selectedHistory.admin_note && (
              <div className="wd-detail-section">
                <h4><FileTextOutlined /> Admin Note</h4>
                <div className="wd-note-box">"{selectedHistory.admin_note}"</div>
              </div>
            )}
          </div>
        )}
      </Drawer>

      {/* ============ REQUEST DRAWER ============ */}
      <Drawer
        title={
          <div className="wd-drawer-title">
            <div className="wd-drawer-title-icon"><WalletOutlined /></div>
            <div>
              <h3>Request Details</h3>
              <p>Withdrawal request info</p>
            </div>
          </div>
        }
        placement="right"
        width={isMobile ? '100%' : 480}
        onClose={() => setRequestDrawerOpen(false)}
        open={requestDrawerOpen}
        className="wd-custom-drawer"
      >
        {selectedRequest && (
          <div className="wd-drawer-content">
            {/* Amount Hero */}
            <div className={`wd-amount-hero ${selectedRequest.status}`}>
              <div className="wd-amount-icon">
                {getStatusConfig(selectedRequest.status).icon}
              </div>
              <div className="wd-amount-value">
                ₹{parseFloat(selectedRequest.amount || 0).toLocaleString("en-IN")}
              </div>
              <div className="wd-amount-ref">
                {generateRefNumber("withdrawal", selectedRequest.id)}
              </div>
              <Tag className={`admin-withdrawals-status-tag admin-withdrawals-status-${getStatusConfig(selectedRequest.status).color}`}>
                {getStatusConfig(selectedRequest.status).icon} {getStatusConfig(selectedRequest.status).label}
              </Tag>
            </div>

            {/* User Info */}
            <div className="wd-detail-section">
              <h4><UserOutlined /> User Info</h4>
              <div className="wd-detail-item">
                <div className="wd-detail-icon-wrap"><UserOutlined /></div>
                <div className="wd-detail-label">Name</div>
                <div className="wd-detail-value">{selectedRequest.user_name || selectedRequest.username || "N/A"}</div>
              </div>
              <div className="wd-detail-item">
                <div className="wd-detail-icon-wrap"><IdcardOutlined /></div>
                <div className="wd-detail-label">Role</div>
                <div className="wd-detail-value">
                  <Tag color={selectedRequest.role === 'staff' ? 'blue' : selectedRequest.role === 'developer' ? 'green' : 'default'}>
                    {selectedRequest.role === 'staff' ? '👔 Staff' : selectedRequest.role === 'developer' ? '💻 Developer' : '👤 User'}
                  </Tag>
                </div>
              </div>
              {selectedRequest.contact && (
                <div className="wd-detail-item">
                  <div className="wd-detail-icon-wrap"><PhoneOutlined /></div>
                  <div className="wd-detail-label">Contact</div>
                  <div className="wd-detail-value">{selectedRequest.contact}</div>
                </div>
              )}
            </div>

            {/* Payment Info */}
            <div className="wd-detail-section">
              <h4><BankOutlined /> Payment Details</h4>
              {selectedRequest.bank_account && (
                <div className="wd-detail-item">
                  <div className="wd-detail-icon-wrap"><BankOutlined /></div>
                  <div className="wd-detail-label">Bank Acc</div>
                  <div className="wd-detail-value mono">{selectedRequest.bank_account}</div>
                </div>
              )}
              {selectedRequest.ifsc_code && (
                <div className="wd-detail-item">
                  <div className="wd-detail-icon-wrap"><IdcardOutlined /></div>
                  <div className="wd-detail-label">IFSC</div>
                  <div className="wd-detail-value mono">{selectedRequest.ifsc_code}</div>
                </div>
              )}
              {selectedRequest.upi_id && (
                <div className="wd-detail-item">
                  <div className="wd-detail-icon-wrap"><CreditCardOutlined /></div>
                  <div className="wd-detail-label">UPI ID</div>
                  <div className="wd-detail-value mono">{selectedRequest.upi_id}</div>
                </div>
              )}
              <div className="wd-detail-item">
                <div className="wd-detail-icon-wrap"><CalendarOutlined /></div>
                <div className="wd-detail-label">Requested</div>
                <div className="wd-detail-value">{formatDateTime(selectedRequest.created_at)}</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="wd-drawer-actions">
              {selectedRequest.status === "pending" && (
                <>
                  <Button
                    icon={<CheckOutlined />}
                    className="wd-action-btn approve"
                    onClick={() => handleRequestAction(selectedRequest, "approved")}
                    loading={actionLoading}
                    block
                  >
                    Approve Request
                  </Button>
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    className="wd-action-btn reject"
                    onClick={() => handleRejectWithNote(selectedRequest)}
                    loading={actionLoading}
                    block
                  >
                    Reject Request
                  </Button>
                </>
              )}
              {selectedRequest.status === "approved" && (
                <Button
                  icon={<SyncOutlined />}
                  className="wd-action-btn process"
                  onClick={() => handleRequestAction(selectedRequest, "processing")}
                  block
                  loading={actionLoading}
                >
                  Mark as Processing
                </Button>
              )}
              {selectedRequest.status === "processing" && (
                <Button
                  icon={<CheckCircleOutlined />}
                  className="wd-action-btn complete"
                  onClick={() => handleRequestAction(selectedRequest, "completed")}
                  block
                  loading={actionLoading}
                >
                  Mark as Completed
                </Button>
              )}
            </div>
          </div>
        )}
      </Drawer>

    </div>
  );
}

export default Withdrawals;